import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/hooks/use-theme";
import { CookieConsentProvider } from "@/lib/cookie-consent";
import { CookieBanner } from "@/components/CookieBanner";
import { CookieSettings } from "@/components/CookieSettings";
import { ScrollToTop } from "./components/ScrollToTop";
import { lazy, Suspense } from "react";

// Eagerly load the main page (critical path)
import Index from "./pages/Index";

// Lazy load all other pages
const CarDetail = lazy(() => import("./pages/CarDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages (heavy, rarely needed by public users)
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminCars = lazy(() => import("./pages/admin/Cars"));
const AdminCarForm = lazy(() => import("./pages/admin/CarForm"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminBanners = lazy(() => import("./pages/admin/Banners"));
const AdminStock = lazy(() => import("./pages/admin/Stock"));
const AdminRanking = lazy(() => import("./pages/admin/Ranking"));
const AdminBlockedIPs = lazy(() => import("./pages/admin/BlockedIPs"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <I18nProvider>
        <CookieConsentProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={null}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Index />} />
                  
                  <Route path="/carro/:id" element={<CarDetail />} />
                  <Route path="/privacidade" element={<PrivacyPolicy />} />
                  <Route path="/auth" element={<Auth />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/carros" element={<AdminCars />} />
                  <Route path="/admin/carros/novo" element={<AdminCarForm />} />
                  <Route path="/admin/carros/:id" element={<AdminCarForm />} />
                  <Route path="/admin/utilizadores" element={<AdminUsers />} />
                  <Route path="/admin/logs" element={<AdminAuditLogs />} />
                  <Route path="/admin/stock" element={<AdminStock />} />
                  <Route path="/admin/banners" element={<AdminBanners />} />
                  <Route path="/admin/ranking" element={<AdminRanking />} />
                  <Route path="/admin/seguranca" element={<AdminBlockedIPs />} />
                  
                  {/* Catch all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CookieBanner />
              <CookieSettings />
            </BrowserRouter>
          </TooltipProvider>
        </CookieConsentProvider>
      </I18nProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
